import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  StockCounts, Locations,
  type StockCountLine, type StockCountStatus, type StorageLocationTreeNode,
} from '@/lib/api';
import {
  Badge, Button, Card, EmptyState, Icon, IconButton, Input, Skeleton, SwitchRow, TableHead,
} from '@/design-system';
import { formatPriceNok, formatDate } from '@/lib/format';
import { useToasts } from '@/lib/store';
import { UpgradeGate, isEntitlementError } from '@/components/UpgradeGate';

const STATUS_TONE: Record<StockCountStatus, 'gold' | 'success'> = {
  open: 'gold',
  completed: 'success',
};

const ROW_COLUMNS = 'minmax(160px,2fr) 70px 90px 110px 130px 90px 150px 60px';

/**
 * Varetelling (stock count) — list of counts on top, the selected count's
 * worksheet below (stock1_ui.webp: search, "Ny telling blir kjellerbeholdning"
 * switch, per-line stepper + confirm, Fullfør + Eksporter PDF). "Volum" is
 * shown but always "—": the Bottle model doesn't track physical bottle
 * volume today (see final report).
 */
export function VaretellingPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const countsQ = useQuery({ queryKey: ['stock-counts'], queryFn: () => StockCounts.list() });

  const createCount = useMutation({
    mutationFn: () => StockCounts.create({}),
    onSuccess: ({ count }) => {
      void qc.invalidateQueries({ queryKey: ['stock-counts'] });
      setSelectedId(count.id);
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  if (isEntitlementError(countsQ.error)) {
    return <UpgradeGate error={countsQ.error} featureLabel="Varetelling" />;
  }

  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="ui-section-title text-3xl">{t('stockCount.title')}</h1>
          <p className="text-sm text-mist">{t('stockCount.subtitle', { count: countsQ.data?.length ?? 0 })}</p>
        </div>
        <Button iconLeft="plus" loading={createCount.isPending} onClick={() => createCount.mutate()}>
          {t('stockCount.new')}
        </Button>
      </header>

      {countsQ.isLoading ? (
        <Skeleton height={120} rounded="md" />
      ) : !countsQ.data?.length ? (
        <EmptyState icon="archive" title={t('stockCount.empty')} />
      ) : (
        <div className="grid gap-2">
          {countsQ.data.map((count) => (
            <button
              key={count.id}
              type="button"
              onClick={() => setSelectedId(count.id)}
              className={`ui-card flex flex-wrap items-center justify-between gap-3 p-3 text-left transition-colors ${
                selectedId === count.id ? 'border-candle bg-candle/5' : 'hover:border-line-soft'
              }`}
            >
              <span className="font-serif text-parchment">{formatDate(count.startedAt)}</span>
              <div className="flex items-center gap-2">
                {count.note && <span className="text-xs text-mist">{count.note}</span>}
                <Badge tone={STATUS_TONE[count.status]}>{t(`stockCount.status.${count.status}`)}</Badge>
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedId && <StockCountEditor countId={selectedId} onClose={() => setSelectedId(undefined)} />}
    </div>
  );
}

function StockCountEditor({ countId, onClose }: { countId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const [search, setSearch] = useState('');
  const [apply, setApply] = useState(true);

  const detailQ = useQuery({ queryKey: ['stock-counts', countId], queryFn: () => StockCounts.get(countId) });
  const locationsQ = useQuery({ queryKey: ['locations.tree'], queryFn: () => Locations.tree(), staleTime: 15_000 });

  const locationName = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (nodes: StorageLocationTreeNode[]) => {
      for (const node of nodes) {
        map.set(node.id, node.name);
        walk(node.children);
      }
    };
    if (locationsQ.data) walk(locationsQ.data.tree);
    return map;
  }, [locationsQ.data]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['stock-counts'] });
    void qc.invalidateQueries({ queryKey: ['stock-counts', countId] });
  };

  const updateLine = useMutation({
    mutationFn: (input: { lineId: string; countedQuantity?: number; confirmed?: boolean }) =>
      StockCounts.updateLine(countId, input.lineId, {
        countedQuantity: input.countedQuantity,
        confirmed: input.confirmed,
      }),
    onSuccess: invalidate,
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const complete = useMutation({
    mutationFn: () => StockCounts.complete(countId, apply),
    onSuccess: (data) => {
      push({
        title: t('stockCount.completed'),
        description: apply ? t('stockCount.adjusted', { count: data.adjustedCount }) : undefined,
        tone: 'success',
      });
      invalidate();
      void qc.invalidateQueries({ queryKey: ['cellar.bottles'] });
      void qc.invalidateQueries({ queryKey: ['locations.tree'] });
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const count = detailQ.data?.count;
  const lines = detailQ.data?.lines ?? [];
  const filtered = useMemo(() => {
    if (!search.trim()) return lines;
    const q = search.trim().toLowerCase();
    return lines.filter(
      (line) => line.name.toLowerCase().includes(q) || (line.producer ?? '').toLowerCase().includes(q),
    );
  }, [lines, search]);
  const confirmedCount = lines.filter((line) => line.confirmed).length;
  const isOpen = count?.status === 'open';

  return (
    <Card className="grid gap-5 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="ui-card-title">{count ? formatDate(count.startedAt) : ''}</h2>
          <p className="text-xs text-mist">{t('stockCount.progress', { done: confirmedCount, total: lines.length })}</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            className="ui-btn ui-btn--ghost ui-btn--sm"
            href={StockCounts.pdfUrl(countId)}
            target="_blank"
            rel="noreferrer"
          >
            <Icon name="download" size={14} />
            {t('stockCount.exportPdf')}
          </a>
          <Button variant="ghost" size="sm" iconLeft="x" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>

      {isOpen && (
        <div className="flex flex-wrap items-center gap-4">
          <Input
            iconLeft="search"
            placeholder={t('stockCount.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
          <SwitchRow
            label={t('stockCount.applySwitch')}
            description={t('stockCount.applySwitchHint')}
            checked={apply}
            onChange={setApply}
          />
        </div>
      )}

      {detailQ.isLoading || !count ? (
        <Skeleton height={300} rounded="md" />
      ) : filtered.length === 0 ? (
        <EmptyState icon="archive" title={t('stockCount.noLines')} />
      ) : (
        <div className="overflow-x-auto">
          <div style={{ minWidth: 900 }}>
            <TableHead
              gridTemplateColumns={ROW_COLUMNS}
              columns={[
                t('stockCount.columns.wine'),
                t('stockCount.columns.vintage'),
                t('stockCount.columns.volume'),
                t('stockCount.columns.purchasePrice'),
                t('stockCount.columns.location'),
                t('stockCount.columns.cellar'),
                t('stockCount.columns.newCount'),
                '',
              ]}
            />
            <div className="grid gap-1 py-1">
              {filtered.map((line) => (
                <StockCountRow
                  key={line.id}
                  line={line}
                  locationName={line.locationId ? locationName.get(line.locationId) : undefined}
                  readOnly={!isOpen}
                  onChange={(value) => updateLine.mutate({ lineId: line.id, countedQuantity: value })}
                  onConfirm={() => updateLine.mutate({ lineId: line.id, confirmed: !line.confirmed })}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div className="flex justify-end border-t border-line pt-4">
          <Button iconLeft="check" loading={complete.isPending} onClick={() => complete.mutate()}>
            {t('stockCount.complete')}
          </Button>
        </div>
      )}
    </Card>
  );
}

function StockCountRow({
  line,
  locationName,
  readOnly,
  onChange,
  onConfirm,
}: {
  line: StockCountLine;
  locationName?: string;
  readOnly: boolean;
  onChange: (value: number) => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const value = line.countedQuantity ?? line.expectedQuantity;
  const changed = value !== line.expectedQuantity;

  return (
    <div style={{ gridTemplateColumns: ROW_COLUMNS }} className="ui-sortable-row items-center">
      <span className="min-w-0 truncate font-serif text-sm text-parchment">
        {[line.producer, line.name].filter(Boolean).join(' — ')}
      </span>
      <span className="text-sm text-mist">{line.vintage ?? '—'}</span>
      <span className="text-sm text-mist">—</span>
      <span className="text-sm text-mist">{formatPriceNok(line.purchasePriceNok)}</span>
      <span className="truncate text-sm text-mist">{locationName ?? t('stockCount.unassigned')}</span>
      <span className="text-sm text-parchment">×{line.expectedQuantity}</span>
      <div className="flex items-center gap-2">
        <IconButton
          icon="minus"
          label={t('stockCount.decrease')}
          size="sm"
          disabled={readOnly || value <= 0}
          onClick={() => onChange(Math.max(0, value - 1))}
        />
        <Badge tone={changed ? 'gold' : 'mist'}>{`×${value}`}</Badge>
        <IconButton icon="plus" label={t('stockCount.increase')} size="sm" disabled={readOnly} onClick={() => onChange(value + 1)} />
      </div>
      <IconButton
        icon="check"
        label={t('stockCount.confirmLine')}
        size="sm"
        variant={line.confirmed ? 'primary' : 'icon'}
        disabled={readOnly}
        onClick={onConfirm}
      />
    </div>
  );
}
